import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { combineReducers } from "redux";

import cartReducer from "./cartReducer";
import locationReducer from "./locationReducer";

// Define RootState type for use throughout the application
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

// Persist configuration
const persistConfig = {
  key: "root",
  version: 1,
  storage,
  whitelist: ["cart", "location"], // Persist cart and location slices
};

// Root reducer - removed cartOperationReducer
const rootReducer = combineReducers({
  cart: cartReducer,
  location: locationReducer,
});

// Persistent reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Store configuration
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "RESET_DELIVERY_FEES_LOADING",
        ],
      },
    }),
  devTools: process.env.NODE_ENV !== "production",
});

// Create a cache for the latest Redux state to ensure it's available
// even when third-party cookies are restricted
let cachedReduxState: RootState | null = null;

// Make getState available for debugging and cartUpdateUtils
if (typeof window !== "undefined") {
  // Define proper state accessor that works even if cookies are restricted
  (window as any).getReduxState = (): RootState | null => {
    try {
      // Get the latest state from the store
      const state = store.getState();

      // Cache the state for fallback access
      cachedReduxState = state;

      console.log("[REDUX-DEBUG] Current Redux state accessed:", {
        isUpdatingFees: state.cart?.isUpdatingFees,
        groupsCount: state.cart?.groups?.length,
        cartVersion: state.cart?.cartVersion,
        timestamp: new Date().toISOString(),
        cookieStatus: navigator.cookieEnabled ? "enabled" : "restricted",
      });

      return state;
    } catch (error) {
      // If there's an error accessing the Redux state directly
      console.warn("[REDUX-DEBUG] Error accessing Redux state:", error);

      // Return the cached state as fallback
      if (cachedReduxState) {
        console.log(
          "[REDUX-DEBUG] Using cached state due to access restrictions"
        );
        return cachedReduxState;
      }

      // Return a minimal fallback state if nothing else is available
      return null;
    }
  };

  // Subscribe to state changes to keep the cache updated
  store.subscribe(() => {
    try {
      cachedReduxState = store.getState();
    } catch (error) {
      console.warn("[REDUX-DEBUG] Error updating cached state:", error);
    }
  });

  // Expose a method to check if the Redux state is accessible
  (window as any).checkReduxStateAccess = () => {
    try {
      const state = store.getState();
      return {
        success: true,
        cookieEnabled: navigator.cookieEnabled,
        hasState: !!state,
        cartVersion: state?.cart?.cartVersion,
      };
    } catch (error) {
      return {
        success: false,
        cookieEnabled: navigator.cookieEnabled,
        error: (error as Error).message,
      };
    }
  };
}

export const persistor = persistStore(store);
export default store;
